// Turning what the USGA knows about a course into Gratigolf's own Course and CourseTees.
import Address, { ADDRESS_TYPES } from '../types/Address';
import Course, { COURSE_TEE_STATUSES, CourseTee } from '../types/Course';
import { DefaultObjects } from '../types/DefaultObjects';
import { UsgaCourseDraft } from '../types/UsgaCourseSearch';

import { guessTeeColor } from './teeColorUtils';
import { generateFirestoreId } from './utils';

/** The men's fields of a local tee, taken from a USGA tee record. */
export function extractMenFields(usgaTee: CourseTee): Partial<CourseTee> {
  return {
    isForMen: true,
    mensRating: usgaTee.mensRating,
    mensSlope: usgaTee.mensSlope,
    ...(usgaTee.mensRatingFront9 !== undefined ? { mensRatingFront9: usgaTee.mensRatingFront9 } : {}),
    ...(usgaTee.mensRatingBack9 !== undefined ? { mensRatingBack9: usgaTee.mensRatingBack9 } : {}),
    ...(usgaTee.mensSlopeFront9 !== undefined ? { mensSlopeFront9: usgaTee.mensSlopeFront9 } : {}),
    ...(usgaTee.mensSlopeBack9 !== undefined ? { mensSlopeBack9: usgaTee.mensSlopeBack9 } : {}),
    mensStrokeIndex: usgaTee.mensStrokeIndex,
    usgaMaleTeeId: usgaTee.itemId,
    usgaMaleTeeSetRatingId: usgaTee.usgaMaleTeeSetRatingId ?? null,
  };
}

/** The women's fields of a local tee, taken from a USGA tee record. */
export function extractWomenFields(usgaTee: CourseTee): Partial<CourseTee> {
  return {
    isForWomen: true,
    womensRating: usgaTee.womensRating,
    womensSlope: usgaTee.womensSlope,
    ...(usgaTee.womensRatingFront9 !== undefined ? { womensRatingFront9: usgaTee.womensRatingFront9 } : {}),
    ...(usgaTee.womensRatingBack9 !== undefined ? { womensRatingBack9: usgaTee.womensRatingBack9 } : {}),
    ...(usgaTee.womensSlopeFront9 !== undefined ? { womensSlopeFront9: usgaTee.womensSlopeFront9 } : {}),
    ...(usgaTee.womensSlopeBack9 !== undefined ? { womensSlopeBack9: usgaTee.womensSlopeBack9 } : {}),
    womensStrokeIndex: usgaTee.womensStrokeIndex,
    usgaFemaleTeeId: usgaTee.itemId,
    usgaFemaleTeeSetRatingId: usgaTee.usgaFemaleTeeSetRatingId ?? null,
  };
}

/** The gender fields a USGA tee record provides — men's, women's, or both. */
export function extractGenderFields(usgaTee: CourseTee): Partial<CourseTee> {
  const isMaleOnly = usgaTee.isForMen === true && usgaTee.isForWomen !== true;
  const isFemaleOnly = usgaTee.isForWomen === true && usgaTee.isForMen !== true;
  if (isMaleOnly) return extractMenFields(usgaTee);
  if (isFemaleOnly) return extractWomenFields(usgaTee);
  return { ...extractMenFields(usgaTee), ...extractWomenFields(usgaTee) };
}

/** A brand-new local tee made from a USGA tee record, with its color guessed from the name. */
export function localTeeFromUsgaTee(usgaTee: CourseTee, localTeeId: string, sortOrder: number): CourseTee {
  const tee: CourseTee = {
    ...usgaTee,
    ...guessTeeColor(usgaTee.name),
    ...extractGenderFields(usgaTee),
    itemId: localTeeId,
    sortOrder,
    status: COURSE_TEE_STATUSES.ACTIVE,
  };
  delete tee.localTeeId;
  return tee;
}

// The USGA names countries by ISO alpha-3; Address wants alpha-2.
const COUNTRY_CODES: Record<string, string> = { USA: 'US', CAN: 'CA', MEX: 'MX', GBR: 'GB', IRL: 'IE', AUS: 'AU', NZL: 'NZ' };

function countryCodeOf(code: string | null): string {
  if (!code) return 'US';
  const upper = code.trim().toUpperCase();
  return COUNTRY_CODES[upper] ?? (upper.length === 2 ? upper : 'US');
}

/** The create page's import mode: the USGA course ids to import, in order, in `usga`. */
export function courseImportPath(usgaCourseIds: string[]): string {
  return `/course-create?usga=${usgaCourseIds.join(',')}`;
}

/** The USGA course ids an import URL names, in order. */
export function usgaCourseIdsOf(searchParams: URLSearchParams): string[] {
  return (searchParams.get('usga') ?? '').split(',').map((id) => id.trim()).filter((id) => /^\d+$/.test(id));
}

export interface CourseFromUsgaDraft {
  course: Course;
  usgaTees: CourseTee[];  // for course/{id}/usga/data, each linked to its local tee
}

/**
 * A new Course seeded from a finished USGA draft: name, address, phone, par, posting season and
 * one local tee per USGA tee set (linked both ways, as "Import" in the USGA tee view links them).
 */
export function courseFromUsgaDraft(draft: UsgaCourseDraft, itemId: string, creatorId: string): CourseFromUsgaDraft {
  const details = draft.course;
  const tees: CourseTee[] = [];
  const usgaTees: CourseTee[] = [];
  (draft.tees ?? []).forEach((usgaTee, index) => {
    const localTeeId = generateFirestoreId();
    tees.push(localTeeFromUsgaTee(usgaTee, localTeeId, index));
    usgaTees.push({ ...usgaTee, localTeeId });
  });

  const hasAddress = !!(details?.street1 || details?.city);
  const streetAddress: Address | undefined = hasAddress && details ? {
    type: ADDRESS_TYPES.STREET,
    isDefault: true,
    name: details.facilityName ?? details.name,
    street1: details.street1 ?? '',
    ...(details.street2 ? { street2: details.street2 } : {}),
    city: details.city ?? '',
    ...(details.state ? { state: details.state } : {}),
    ...(details.postalCode ? { postalCode: details.postalCode } : {}),
    countryCode: countryCodeOf(details.countryCode),
  } : undefined;

  const par = tees[0]?.par?.reduce((sum, holePar) => sum + holePar, 0) ?? 0;

  const course: Course = {
    ...DefaultObjects.Course,
    itemId,
    creatorId,
    name: details?.name ?? '',
    ...(streetAddress ? { streetAddress } : {}),
    ...(details?.phone ? { phone: details.phone } : {}),
    par,
    tees,
    usgaCourseId: draft.itemId,
    usgaSeason: details?.season ?? null,
  };
  return { course, usgaTees };
}
